'use client';

import { c as _c } from "react/compiler-runtime";
import { fixWorkstationsFromCsv, type WorkstationFixResult, type WorkstationFixRow } from '@/app/_workflows/fixWorkstationsFromCsv';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
interface FixWorkstationsFromCsvModalProps {
  isOpen: boolean;
  onClose(): void;
}
const NAME_COL = 'name';
const IS_ACTIVE_COL = 'isActive';
const SITES_COL = 'sites';
const OPERATIONS_COL = 'operationTableNames';
const splitList = (value: unknown, separator: string) => String(value ?? '').split(separator).map(item => item.trim()).filter(Boolean);
function parseFile(file: File): Promise<WorkstationFixRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target!.result as ArrayBuffer), {
          type: 'array',
          raw: true
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: null,
          raw: false
        });
        resolve(rows.map(row => ({
          name: String(row[NAME_COL] ?? '').trim(),
          isActive: ['true', '1'].includes(String(row[IS_ACTIVE_COL] ?? '').trim().toLowerCase()),
          sites: splitList(row[SITES_COL], '|'),
          operationTableNames: splitList(row[OPERATIONS_COL], ',')
        })).filter(row => row.name !== ''));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
const ResultList: React.FC<{
  title: string;
  color: string;
  items: string[];
}> = t0 => {
  const $ = _c(4);
  const {
    title,
    color,
    items
  } = t0;
  let t1;
  if ($[0] !== color || $[1] !== items || $[2] !== title) {
    t1 = items.length === 0 ? null : <Box><Typography variant="subtitle2" color={color} gutterBottom={true}>{title} ({items.length})</Typography><Divider sx={{
        mb: 1
      }} /><List dense={true} disablePadding={true} sx={{
        maxHeight: 200,
        overflowY: "auto"
      }}>{items.map(_temp)}</List></Box>;
    $[0] = color;
    $[1] = items;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const FixWorkstationsFromCsvModal: React.FC<FixWorkstationsFromCsvModalProps> = t0 => {
  const $ = _c(29);
  const {
    isOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rows, setRows] = useState(t1);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t2;
  if ($[1] !== isLoading || $[2] !== onClose) {
    t2 = () => {
      if (isLoading) {
        return;
      }
      setRows([]);
      setFileName(null);
      setParseError(null);
      setResult(null);
      onClose();
    };
    $[1] = isLoading;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClose = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async file => {
      setParseError(null);
      setRows([]);
      setFileName(file.name);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setParseError(`No valid rows found. The file needs a header row with "${NAME_COL}", "${IS_ACTIVE_COL}", "${SITES_COL}" and "${OPERATIONS_COL}".`);
          return;
        }
        setRows(parsed);
      } catch {
        setParseError("Failed to parse file. Make sure it is a valid CSV file.");
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const processFile = t3;
  let t4;
  if ($[5] !== doServerAction || $[6] !== rows) {
    t4 = () => {
      doServerAction(async () => ({
        data: await fixWorkstationsFromCsv(rows)
      }), response => {
        if (!response || typeof response !== "object" || !("data" in response)) {
          return;
        }
        setResult(response.data);
      });
    };
    $[5] = doServerAction;
    $[6] = rows;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleApply = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== handleApply || $[10] !== handleClose || $[11] !== isLoading || $[12] !== result || $[13] !== rows) {
    t6 = result ? <Button variant="contained" onClick={handleClose}>Close</Button> : <Button variant="contained" disabled={rows.length === 0 || isLoading} onClick={handleApply}>Fix workstations {rows.length > 0 ? `(${rows.length} rows)` : ""}</Button>;
    $[9] = handleApply;
    $[10] = handleClose;
    $[11] = isLoading;
    $[12] = result;
    $[13] = rows;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== fileName || $[16] !== parseError || $[17] !== result || $[18] !== rows) {
    t7 = !result && <><Box onClick={() => fileInputRef.current?.click()} sx={{
        mt: 1,
        mb: 2,
        border: 2,
        borderStyle: "dashed",
        borderColor: "divider",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: "pointer"
      }}><CloudUploadIcon sx={{
          fontSize: 40,
          color: "text.secondary"
        }} /><Typography variant="body2" color="text.secondary">Click to select the CSV file</Typography><input ref={fileInputRef} type="file" accept=".csv" style={{
          display: "none"
        }} onChange={e => {
          const file_0 = e.target.files?.[0];
          if (file_0) {
            processFile(file_0);
          }
          e.target.value = "";
        }} /></Box>{parseError && <Alert severity="error">{parseError}</Alert>}{rows.length > 0 && <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><Chip label={`${rows.length} rows ready`} color="success" size="small" />{fileName && <Typography variant="caption" color="text.secondary">{fileName}</Typography>}</Box>}</>;
    $[15] = fileName;
    $[16] = parseError;
    $[17] = result;
    $[18] = rows;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== result) {
    t8 = result && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><CheckCircleOutlineIcon color={result.errors.length > 0 ? "warning" : "success"} /><Typography fontWeight={500}>{result.updated} workstations fixed</Typography></Box><ResultList title="Errors" color="error.main" items={result.errors} /><ResultList title="Sites not found (left out of the workstation)" color="warning.main" items={result.unmatchedSites.map(_temp2)} /><ResultList title={"Skipped \u2014 repeated name"} color="warning.main" items={result.ambiguous} /><ResultList title={"Skipped \u2014 not found here"} color="text.secondary" items={result.notFound} /></Box>;
    $[20] = result;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== handleClose || $[23] !== isLoading || $[24] !== isOpen || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <Modal isOpen={isOpen} onClose={handleClose} title="Fix workstations from CSV" subTitle="Upload the CSV exported from prod with scripts/export-workstations.sql. Workstations are matched by name and get their active status, sites (matched by name) and operations replaced. Workstations not found here are skipped." maxWidth="sm" fullWidth={true} disableClose={isLoading} isLoading={isLoading} ContentProps={t5} actions={t6}>{t7}{t8}</Modal>;
    $[22] = handleClose;
    $[23] = isLoading;
    $[24] = isOpen;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
function _temp(item) {
  return <ListItem key={item} disableGutters={true}><ListItemText primary={item} primaryTypographyProps={{
      variant: "body2",
      color: "text.secondary"
    }} /></ListItem>;
}
function _temp2(t0) {
  const {
    workstation,
    sites
  } = t0;
  return `${workstation}: ${sites.join(", ")}`;
}